import { VaultObjectUploadData } from "@/components/vault/vault-object-util";
import {
  LoaderData,
  VaultObjectViewerData,
} from "@/components/vault/viewer/VaultObjectViewerModal";
import { AssetData } from "@/types/data-types";
import { License, LICENSE_LABEL, VAULT_OBJECT_TYPE } from "@/types/enums";
import { printBytesPretty } from "@/utils/fileUtil";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import { motion } from "motion/react";
import React from "react";
import Link from "next/link";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";

interface MetadataTabContentProps {
  loaderData: LoaderData | null;
  vaultObjectViewerData: VaultObjectViewerData | null;
  asset:
    | { type: typeof VAULT_OBJECT_TYPE.OBJECT; data: AssetData }
    | { type: typeof VAULT_OBJECT_TYPE.OBJECT; data: VaultObjectUploadData }
    | null;
  newLicense: HUI.SelectItemInterface<License> | null;
  setNewLicense: React.Dispatch<
    React.SetStateAction<HUI.SelectItemInterface<License> | null>
  >;
}

export const MetadataTabContent: React.FC<MetadataTabContentProps> = ({
  // gaiaStore,
  // loaderData,
  vaultObjectViewerData,
  asset,
  newLicense,
  setNewLicense,
}) => {
  const currentPage = useVaultStore((state) => state.currentPage);

  const uploadData = useVaultStore((state) => state.uploadData);

  let format = "N/A";
  switch (currentPage) {
    case "viewer": {
      if (
        asset?.type === VAULT_OBJECT_TYPE.OBJECT &&
        vaultObjectViewerData?.assetData?.extension
      ) {
        format = vaultObjectViewerData.assetData.extension;
      }
      break;
    }
    case "uploader":
    case "uploader-thumbnail":
      if (uploadData) {
        format = uploadData.extension;
      }
      break;
  }

  let size = NaN;
  switch (currentPage) {
    case "viewer": {
      if (asset) {
        size = (asset.data as AssetData).fileList.reduce(
          (acc, file) => acc + file.bytes,
          0,
        );
      }
      break;
    }
    case "uploader":
    case "uploader-thumbnail":
      if (uploadData?.file) {
        size = uploadData.file.size;
      }
      break;
  }

  if (vaultObjectViewerData?.embed) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="w-full flex gap-4 overflow-visible items-center p-px"
    >
      {/* Metadata */}
      <div className="flex flex-row gap-3">
        <div className="flex justify-center items-center flex-shrink-0 h-10 gap-2 px-3 bg-white rounded-lg shadow-[0_0_4px_0_rgba(0,0,0,0.1)]">
          <HUI.Text
            kind={HENGE.TEXT_KIND.small}
            lineHeight={HENGE.TEXT_LINE_HEIGHT.small}
            whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
            colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
            className="select-none"
          >
            포맷
          </HUI.Text>
          <HUI.Text
            kind={HENGE.TEXT_KIND.body2}
            whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
            colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
            weight={HENGE.TEXT_WEIGHT.medium}
            className="select-none"
          >
            {format}
          </HUI.Text>
        </div>

        {!isNaN(size) && (
          <>
            <div className="flex justify-center items-center flex-shrink-0 gap-2 px-3 h-10 bg-white rounded-lg shadow-[0_0_4px_0_rgba(0,0,0,0.1)]">
              <HUI.Text
                kind={HENGE.TEXT_KIND.small}
                lineHeight={HENGE.TEXT_LINE_HEIGHT.small}
                whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                className="select-none"
              >
                크기
              </HUI.Text>
              <HUI.Text
                kind={HENGE.TEXT_KIND.body2}
                colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
                weight={HENGE.TEXT_WEIGHT.medium}
                whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                className="select-none"
              >
                {/* Add file size calculation */}
                {printBytesPretty(size)}
              </HUI.Text>
            </div>
          </>
        )}

        {vaultObjectViewerData?.license && (
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center px-3 flex-shrink-0 gap-2 h-10 bg-white rounded-lg shadow-[0_0_4px_0_rgba(0,0,0,0.1)]">
              <HUI.Text
                kind={HENGE.TEXT_KIND.small}
                lineHeight={HENGE.TEXT_LINE_HEIGHT.small}
                whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                className="select-none"
              >
                라이선스
              </HUI.Text>
              <HUI.Text
                kind={HENGE.TEXT_KIND.body2}
                colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
                weight={HENGE.TEXT_WEIGHT.medium}
                whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                className="select-none"
              >
                {LICENSE_LABEL[vaultObjectViewerData.license]}
              </HUI.Text>
              {vaultObjectViewerData.credit && (
                <>
                  <div className="w-px h-4 bg-[var(--border-tertiary)] flex-shrink-0 mx-2" />
                  <HUI.Text
                    kind={HENGE.TEXT_KIND.small}
                    lineHeight={HENGE.TEXT_LINE_HEIGHT.small}
                    whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                    colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                    className="select-none"
                  >
                    크레딧
                  </HUI.Text>
                  <HUI.Text
                    kind={HENGE.TEXT_KIND.body2}
                    colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
                    weight={HENGE.TEXT_WEIGHT.medium}
                    whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                    link={
                      <Link
                        href={vaultObjectViewerData.credit.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    }
                    className="select-none"
                  >
                    {vaultObjectViewerData.credit.creator}
                  </HUI.Text>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
